import { useState, memo } from "react";
import { Popup } from "components/UI/Popup";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "store/app-state";
import { addGifToCollection } from "store/collections/collections-action";
import { CreateNewCollectionPopup } from "./CreateNewCollectionPopup";
import { useIsAuth } from "hooks/useIsAuth";
import { showAuthModal } from "store/application/application-action";
import { metrics } from "utils/metrics";
import { CollectionItem } from "./CollectionItem";
import { BaseButton } from "components/buttons/BaseButton";
import "./styles/AddToCollectionPopup.scss";
import InfiniteScroll from "react-infinite-scroll-component";
import { useUserCollectionsData } from "hooks/useUserCollectionsData";

interface IAddToCollectionPopupProps {
  isOpen: boolean;
  togglePopup: () => void;
  gifId: string;
}

export const AddToCollectionPopup = memo((props: IAddToCollectionPopupProps) => {
  const { isOpen, togglePopup, gifId } = props;
  const userName = useSelector((state: IAppState) => state.user.currentUser.username);
  const [isNewCollectionPopupOpen, setIsOpenNewCollectionPopup] = useState(false);
  const isAuth = useIsAuth();
  const dispatch = useDispatch();

  const {
    items,
    isError,
    hasNextPage,
    fetchNextPage
  } = useUserCollectionsData({
    userName,
    isRefetch: isNewCollectionPopupOpen
  });

  const toggleCreateNewCollectionPopup = () => {
    setIsOpenNewCollectionPopup(prev => !prev);
  };

  const addToCollections = async (folderId: string) => {
    dispatch(addGifToCollection({
      collectionId: folderId,
      gifId: gifId
    }));
    await metrics.sendAddedToCollection({ gif: gifId });
    togglePopup();
  };

  const handleLogin = () => {
    dispatch(showAuthModal());
  };

  const btnProps = {
    type: 2,
    title: "Create a new collection",
    onClick: toggleCreateNewCollectionPopup,
    btnType: "submit"
  };

  return (
    <Popup
      title="Add to collection"
      isOpen={isOpen}
      popupClass="AddToCollectionPopup"
      btnProps={btnProps}
      onClose={togglePopup}
    >
      {isAuth && (
        <InfiniteScroll
          dataLength={items.length}
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={<></>}
          scrollableTarget="scrollable-container"
        >
          <div id="scrollable-container">
            <ul className="AddToCollectionPopup-List">
              {isError ? (
                <li className="AddToCollectionPopup-NoCollection">
                  We have some issues with loading collections. Please close and open the dialog and try again.
                </li>
              ) : (
                <>
                  {items?.map((collection: any) => (
                    <CollectionItem
                      key={collection.folderId}
                      collection={collection}
                      addToCollections={addToCollections}
                    />
                  ))}
                </>
              )}
            </ul>
          </div>
        </InfiniteScroll>
      )}
      {!isAuth && (
        <div className="AddToCollectionPopup-Login">
          <p>You’ve gotta log in to add to collections to RedGIFs!</p>
          <BaseButton onClick={handleLogin}>Login</BaseButton>
        </div>
      )}
      {isNewCollectionPopupOpen &&
        <CreateNewCollectionPopup
          isOpen={isNewCollectionPopupOpen}
          togglePopup={toggleCreateNewCollectionPopup}
        />
      }
    </Popup>
  );
});
